import React from "react";
import Box from "@mui/material/Box";

export const NOTIFICATIONS_TAB_KEY = "notificationsTab";
const TABLER_ICON_IMAGE_CDN_BASE = "https://cdn.jsdelivr.net/npm/@tabler/icons/icons/outline";
const MENU_ICON_UNIFORM_SIZE = 18;

export function looksLikeJson(source) {
  if (!source || typeof source !== "string") {
    return false;
  }
  const first = source.trim()[0];
  return first === "{" || first === "[";
}

export function normalizeExtensions(data) {
  if (!data) {
    return [];
  }
  if (Array.isArray(data)) {
    return data;
  }
  if (Array.isArray(data.items)) {
    return data.items;
  }
  if (Array.isArray(data.menu)) {
    return data.menu;
  }
  if (Array.isArray(data.menus)) {
    return data.menus;
  }
  return [];
}

function pickField(obj, keys, fallback = undefined) {
  if (!obj || typeof obj !== "object") {
    return fallback;
  }
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(obj, key) && obj[key] != null) {
      return obj[key];
    }
  }
  return fallback;
}

function toSortNumber(value) {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeMenuModule(item) {
  if (!item || typeof item !== "object") {
    return item;
  }
  const children = pickField(item, ["children", "Children"], []);
  return {
    ...item,
    type: pickField(item, ["type", "Type"], ""),
    title: pickField(item, ["title", "Title"], ""),
    name: pickField(item, ["name", "Name"], ""),
    code: pickField(item, ["code", "Code"], ""),
    url: pickField(item, ["url", "Url"], ""),
    icon: pickField(item, ["icon", "Icon"], ""),
    children: Array.isArray(children) ? children.map(normalizeMenuModule) : [],
  };
}

function normalizeMenuApp(item) {
  if (!item || typeof item !== "object") {
    return item;
  }
  const modules = pickField(item, ["modules", "Modules"], []);
  return {
    ...item,
    type: pickField(item, ["type", "Type"], ""),
    title: pickField(item, ["title", "Title"], ""),
    code: pickField(item, ["code", "Code"], ""),
    name: pickField(item, ["name", "Name"], ""),
    url: pickField(item, ["url", "Url"], ""),
    icon: pickField(item, ["icon", "Icon"], ""),
    logo: pickField(item, ["logo", "Logo"], ""),
    sort: pickField(item, ["sort", "Sort", "order", "Order"], null),
    modules: Array.isArray(modules) ? modules.map(normalizeMenuModule) : [],
  };
}

function sortMenuApps(menuItems) {
  if (!Array.isArray(menuItems) || menuItems.length === 0) {
    return [];
  }

  return menuItems
    .map((item, index) => ({ item: normalizeMenuApp(item), index }))
    .sort((left, right) => {
      const leftSort = toSortNumber(left.item?.sort);
      const rightSort = toSortNumber(right.item?.sort);

      if (leftSort !== null && rightSort !== null && leftSort !== rightSort) {
        return leftSort - rightSort;
      }
      if (leftSort !== null && rightSort === null) {
        return -1;
      }
      if (leftSort === null && rightSort !== null) {
        return 1;
      }

      return left.index - right.index;
    })
    .map(({ item }) => item);
}

export function isSeparator(item) {
  if (!item || typeof item !== "object") {
    return false;
  }
  const type = String(pickField(item, ["type", "Type"], "") || "").toLowerCase();
  return type === "separator" || type === "title";
}

export function getSeparatorTitle(item) {
  if (!item || typeof item !== "object") {
    return "";
  }
  const title = pickField(item, ["title", "Title"], "");
  if (typeof title === "string" && title.trim() !== "") {
    return title.trim();
  }
  const type = String(pickField(item, ["type", "Type"], "") || "").toLowerCase();
  const name = pickField(item, ["name", "Name"], "");
  if (type === "title" && typeof name === "string") {
    return name.trim();
  }
  return "";
}

export function hasModules(item) {
  return Array.isArray(item?.modules) && item.modules.length > 0;
}

export function hasChildren(item) {
  return Array.isArray(item?.children) && item.children.length > 0;
}

export function mergeMenuExtensions(menuItems, extensions, position = "bottom") {
  const base = Array.isArray(menuItems) ? menuItems.map(normalizeMenuApp) : [];
  if (!Array.isArray(extensions) || extensions.length === 0) {
    return base;
  }
  const normalizedExtensions = extensions.map(normalizeMenuApp);
  const insertAtStart = ["top", "before", "prepend"].includes(String(position || "").toLowerCase());
  return insertAtStart ? [...normalizedExtensions, ...base] : [...base, ...normalizedExtensions];
}

function normalizeLookupKey(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizeOverrideOrigin(value) {
  const source = String(value || "").trim();
  if (!source) {
    return "";
  }
  try {
    const url = source.startsWith("//")
      ? new URL(`${window.location.protocol}${source}`)
      : new URL(source, window.location.origin);
    if (!url.origin || url.origin === "null") {
      return "";
    }
    return url.origin;
  } catch (_error) {
    return "";
  }
}

function rewriteLinkToOrigin(url, origin) {
  const value = String(url || "").trim();
  if (!value || value === "#") {
    return value;
  }
  if (value.startsWith("mailto:") || value.startsWith("tel:")) {
    return value;
  }
  try {
    const current = new URL(value, window.location.origin);
    const next = new URL(origin);
    next.pathname = current.pathname;
    next.search = current.search;
    next.hash = current.hash;
    return next.toString();
  } catch (_error) {
    return value;
  }
}

function rewriteModuleTree(item, origin) {
  if (!item || typeof item !== "object") {
    return item;
  }
  const children = Array.isArray(item.children)
    ? item.children.map((child) => rewriteModuleTree(child, origin))
    : [];
  return {
    ...item,
    url: rewriteLinkToOrigin(item.url || "#", origin),
    children,
  };
}

export function parseDevOverrides(source) {
  if (!source || typeof source !== "string") {
    return {};
  }
  try {
    const raw = JSON.parse(source);
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
      return {};
    }
    const normalized = {};
    Object.entries(raw).forEach(([key, value]) => {
      const lookup = normalizeLookupKey(key);
      const origin = normalizeOverrideOrigin(value);
      if (!lookup || !origin) {
        return;
      }
      normalized[lookup] = origin;
    });
    return normalized;
  } catch (_error) {
    return {};
  }
}

export function applyMenuDevOverrides(menuItems, overrideMap) {
  const base = Array.isArray(menuItems) ? menuItems : [];
  const hasOverrides = overrideMap && typeof overrideMap === "object" && Object.keys(overrideMap).length > 0;
  if (!hasOverrides) {
    return base;
  }

  return base.map((item) => {
    if (!item || typeof item !== "object" || isSeparator(item)) {
      return item;
    }

    const codeKey = normalizeLookupKey(item.code);
    const nameKey = normalizeLookupKey(item.name);
    const origin = overrideMap[codeKey] || overrideMap[nameKey];
    if (!origin) {
      return item;
    }

    return {
      ...item,
      url: rewriteLinkToOrigin(item.url || "#", origin),
      modules: Array.isArray(item.modules)
        ? item.modules.map((module) => rewriteModuleTree(module, origin))
        : [],
    };
  });
}

export function unwrapResponsePayload(payload) {
  if (!payload || typeof payload !== "object") {
    return payload;
  }
  if ("data" in payload && payload.data && typeof payload.data === "object") {
    return payload.data;
  }
  if ("body" in payload && payload.body && typeof payload.body === "object") {
    return payload.body;
  }
  return payload;
}

export function normalizeMenuPayload(rawPayload, defaults = {}) {
  const payload = unwrapResponsePayload(rawPayload);
  const base = {
    menu: [],
    css: [],
    username: defaults.username || "",
    userAvatarLetter: defaults.userAvatarLetter || "U",
    logoutUrl: defaults.logoutUrl || "/logout",
    perfilUrl: defaults.perfilUrl || `${defaults.hostBase || ""}/-/perfil`,
    notifications: {
      total: 0,
      unread_total: 0,
      status_text: "Todo al dia",
      items: [],
    },
  };

  if (Array.isArray(payload)) {
    return {
      ...base,
      menu: sortMenuApps(payload),
    };
  }

  if (!payload || typeof payload !== "object") {
    return base;
  }

  const notifications = payload.notifications || payload.Notifications || {};
  const notificationItems =
    notifications.items || notifications.Items || payload.notification_items || payload.NotificationItems || [];

  return {
    ...base,
    menu: sortMenuApps(payload.menu || payload.Menu || []),
    css: payload.css || payload.Css || [],
    username: payload.username || payload.Username || base.username,
    userAvatarLetter:
      payload.user_avatar_letter || payload.userAvatarLetter || payload.UserAvatarLetter || base.userAvatarLetter,
    logoutUrl: payload.logout_url || payload.logoutUrl || payload.LogoutUrl || base.logoutUrl,
    perfilUrl: payload.perfil_url || payload.perfilUrl || payload.PerfilUrl || base.perfilUrl,
    notifications: {
      total: notifications.total || notifications.Total || 0,
      unread_total: notifications.unread_total || notifications.UnreadTotal || 0,
      status_text: notifications.status_text || notifications.StatusText || "Todo al dia",
      items: Array.isArray(notificationItems) ? notificationItems : [],
    },
  };
}

export function looksLikeImageUrl(value) {
  return (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("/") ||
    value.startsWith("./") ||
    value.startsWith("../") ||
    value.startsWith("data:image/")
  );
}

function extractHtmlAttribute(value, attrName) {
  const regex = new RegExp(`${attrName}\\s*=\\s*("([^"]*)"|'([^']*)')`, "i");
  const match = String(value || "").match(regex);
  return (match?.[2] || match?.[3] || "").trim();
}

function isSvgSource(value) {
  const source = String(value || "").trim().toLowerCase();
  if (!source) {
    return false;
  }
  if (source.startsWith("data:image/svg+xml")) {
    return true;
  }
  return /\.svg(?:[?#].*)?$/.test(source);
}

function extractTablerIconName(value) {
  const text = String(value || "").trim();
  if (!text) return "";

  const fromClass = text.match(/(?:^|\s)ti\s+ti-([a-z0-9-]+)/i);
  if (fromClass?.[1]) return fromClass[1].toLowerCase();

  const fromShortClass = text.match(/(?:^|\s)ti-([a-z0-9-]+)/i);
  if (fromShortClass?.[1]) return fromShortClass[1].toLowerCase();

  const fromDataAttr = text.match(/data-tabler-icon\s*=\s*"([a-z0-9-]+)"/i) ||
    text.match(/data-tabler-icon\s*=\s*'([a-z0-9-]+)'/i);
  if (fromDataAttr?.[1]) return fromDataAttr[1].toLowerCase();

  const fromSvgClass = text.match(/icon-tabler-([a-z0-9-]+)/i);
  if (fromSvgClass?.[1]) return fromSvgClass[1].toLowerCase();

  return "";
}

function parseIconSpec(icon) {
  if (!icon || typeof icon !== "string") {
    return { kind: "none" };
  }

  const value = icon.trim();
  if (!value) {
    return { kind: "none" };
  }

  if (value.startsWith("<svg")) {
    return { kind: "svg", markup: sanitizeSvgMarkup(value) };
  }

  if (value.startsWith("<img")) {
    const src = extractHtmlAttribute(value, "src");
    if (src) {
      const alt = extractHtmlAttribute(value, "alt") || "icon";
      return { kind: "image", src, alt };
    }
  }

  if (looksLikeImageUrl(value)) {
    return { kind: "image", src: value, alt: "icon" };
  }

  if (value.startsWith("<span") || value.startsWith("<i")) {
    const text = value.replace(/<[^>]+>/g, "").trim();
    if (text) {
      return { kind: "text", value: text.slice(0, 2) };
    }
  }

  const tablerName = extractTablerIconName(value);
  if (tablerName) {
    return {
      kind: "image",
      src: `${TABLER_ICON_IMAGE_CDN_BASE}/${tablerName}.svg`,
      alt: tablerName,
    };
  }

  return { kind: "none" };
}

function sanitizeSvgMarkup(markup) {
  if (!markup || typeof markup !== "string") {
    return "";
  }
  return String(markup)
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/\s+on[a-z]+\s*=\s*(".*?"|'.*?'|[^\s>]+)/gi, "")
    .replace(/\s+(href|xlink:href)\s*=\s*("javascript:[^"]*"|'javascript:[^']*'|javascript:[^\s>]+)/gi, "")
    .replace(/<svg\b([^>]*)>/i, (_full, attrs = "") => {
      const cleanAttrs = String(attrs)
        .replace(/\s+(width|height|style)\s*=\s*(".*?"|'.*?'|[^\s>]+)/gi, "");
      return `<svg${cleanAttrs}>`;
    });
}

export function toAbsoluteUrl(url, hostBase = "") {
  const value = String(url || "").trim();
  if (!value) {
    return "#";
  }
  if (value.startsWith("http://") || value.startsWith("https://") || value.startsWith("mailto:") || value.startsWith("tel:")) {
    return value;
  }
  if (value.startsWith("//")) {
    return `${window.location.protocol}${value}`;
  }
  if (value.startsWith("#")) {
    return value;
  }
  if (hostBase && value.startsWith("/-/")) {
    return `${hostBase.replace(/\/$/, "")}${value}`;
  }
  return value;
}

export function readNotificationIcon(item) {
  if (!item || typeof item !== "object") {
    return "";
  }
  return item.icon || item.ico || item.Icon || item.Ico || "";
}

export function MenuIcon({ icon, label = "" }) {
  const iconSpec = parseIconSpec(icon);
  const size = MENU_ICON_UNIFORM_SIZE;

  if (iconSpec.kind === "svg" && iconSpec.markup) {
    return (
      <Box
        component="span"
        sx={{
          width: size,
          height: size,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          overflow: "hidden",
          lineHeight: 0,
          "& > svg": {
            width: `${size}px`,
            height: `${size}px`,
            display: "block",
            maxWidth: "100%",
            maxHeight: "100%",
            color: "currentColor",
          },
          "& > svg, & > svg *": {
            color: "currentColor",
            maxWidth: "100%",
            maxHeight: "100%",
          },
          "& > svg *:not([fill])": {
            fill: "currentColor !important",
          },
          "& > svg [fill]:not([fill='none'])": {
            fill: "currentColor !important",
          },
          "& > svg [stroke]:not([stroke='none'])": {
            stroke: "currentColor !important",
          },
        }}
        dangerouslySetInnerHTML={{ __html: iconSpec.markup }}
      />
    );
  }

  if (iconSpec.kind === "image" && iconSpec.src) {
    return (
      <Box
        component="span"
        role="img"
        aria-label={iconSpec.alt || "icon"}
        sx={{
          width: size,
          height: size,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          overflow: "hidden",
          backgroundColor: "currentColor",
          maskImage: `url("${iconSpec.src}")`,
          maskRepeat: "no-repeat",
          maskSize: "contain",
          maskPosition: "center",
          WebkitMaskImage: `url("${iconSpec.src}")`,
          WebkitMaskRepeat: "no-repeat",
          WebkitMaskSize: "contain",
          WebkitMaskPosition: "center",
        }}
      />
    );
  }

  if (iconSpec.kind === "text" && iconSpec.value) {
    return (
      <Box
        component="span"
        sx={{
          width: size,
          height: size,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "50%",
          fontSize: 11,
          fontWeight: 700,
          lineHeight: 1,
          flexShrink: 0,
          overflow: "hidden",
        }}
      >
        {iconSpec.value}
      </Box>
    );
  }

  return (
    <Box
      component="span"
      sx={{
        width: size,
        height: size,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: "50%",
        fontSize: 11,
        fontWeight: 700,
      }}
    >
      {String(label || "").slice(0, 1).toUpperCase() || "•"}
    </Box>
  );
}

export function SectionSeparator({ title = "" }) {
  return (
    <Box
      role="separator"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: 1,
        py: 0.75,
        opacity: 0.72,
      }}
    >
      <Box sx={{ flex: 1, height: 1, bgcolor: "divider" }} />
      {title ? (
        <Box component="span" sx={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
          {title}
        </Box>
      ) : null}
      <Box sx={{ flex: 1, height: 1, bgcolor: "divider" }} />
    </Box>
  );
}
